import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, PhoneCall } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, COMPANY } from "../lib/site";

const toProp = (l) =>
    l.kind === "route"
        ? l.path
        : l.id === "top"
          ? { pathname: "/" }
          : { pathname: "/", hash: `#${l.id}` };

export default function HeaderNav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const isHome = pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => (document.body.style.overflow = "");
    }, [open]);

    // Solid (light) nav whenever scrolled, menu open, or on a non-home route
    const solid = scrolled || open || !isHome;
    const isActive = (l) => l.kind === "route" && pathname === l.path;

    return (
        <header
            data-testid="nav-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                solid
                    ? "border-b border-slate-200/70 bg-white/85 backdrop-blur-xl shadow-[0_8px_30px_rgba(15,23,42,0.06)]"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <nav
                className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-12"
                aria-label="Primary"
            >
                <Link
                    to="/"
                    data-testid="nav-logo"
                    aria-label="Lynxtech IT Solutions home"
                    className="shrink-0"
                >
                    <Logo dark={!solid} />
                </Link>

                <div className="hidden items-center gap-1 lg:flex">
                    {NAV_LINKS.map((l) => (
                        <Link
                            key={l.label}
                            to={toProp(l)}
                            data-testid={l.testid}
                            aria-current={isActive(l) ? "page" : undefined}
                            className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                isActive(l)
                                    ? "text-brand"
                                    : solid
                                      ? "text-slate-600 hover:bg-slate-100 hover:text-brand"
                                      : "text-slate-200 hover:bg-white/10 hover:text-white"
                            }`}
                        >
                            {l.label}
                            {isActive(l) && (
                                <span className="absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-brand" />
                            )}
                        </Link>
                    ))}
                </div>

                <div className="flex items-center gap-2">
                    <Link
                        to={{ pathname: "/", hash: "#contact" }}
                        data-testid="nav-cta"
                        className="hidden items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(2,132,199,0.35)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-600 sm:inline-flex"
                    >
                        <PhoneCall className="h-4 w-4" /> Get in Touch
                    </Link>

                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        className={`inline-flex h-11 w-11 items-center justify-center rounded-xl transition-colors lg:hidden ${
                            solid ? "bg-slate-100 text-navy" : "bg-white/10 text-white"
                        }`}
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden"
                    >
                        <div className="mx-3 mb-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
                            {NAV_LINKS.map((l) => (
                                <Link
                                    key={l.label}
                                    to={toProp(l)}
                                    onClick={() => setOpen(false)}
                                    data-testid={`${l.testid}-mobile`}
                                    aria-current={isActive(l) ? "page" : undefined}
                                    className={`block rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                                        isActive(l)
                                            ? "bg-brand-50 text-brand"
                                            : "text-slate-700 hover:bg-brand-50 hover:text-brand"
                                    }`}
                                >
                                    {l.label}
                                </Link>
                            ))}
                            <Link
                                to={{ pathname: "/", hash: "#contact" }}
                                onClick={() => setOpen(false)}
                                data-testid="nav-cta-mobile"
                                className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-base font-semibold text-white"
                            >
                                <PhoneCall className="h-4 w-4" /> Get in Touch
                            </Link>
                            <a
                                href={COMPANY.phoneHref}
                                className="mt-1 block px-4 py-2 text-center font-mono text-xs tracking-wide text-slate-400"
                            >
                                {COMPANY.phoneDisplay}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
